/**
 * Community / Doubts — every question a learner has raised in the lesson panel of the staff
 * member's courses, in one list: student, course, lesson, the question, when, answered or not.
 * Opening a row shows the whole thread in the side pane, where the tutor replies and marks it
 * answered; the reply appears straight away in the learner's Community tab.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import SplitLayout, { PaneFooter } from '../../components/shell/SidePane';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ChatsCircle, CheckCircle, Question, Megaphone, ArrowBendUpLeft, Trash, Play, ChalkboardTeacher } from '@phosphor-icons/react';

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const clock = (s) => (s === null || s === undefined ? null : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`);
const isTutor = (role) => ['instructor', 'tenant_admin', 'super_admin'].includes(role);
const initials = (n = '') => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

export default function Community() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ posts: [], courses: [], unanswered: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'unanswered', course_id: '', kind: '' });
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
      if (filters.course_id) qs.set('course_id', filters.course_id);
      if (filters.kind) qs.set('kind', filters.kind);
      const r = await api.get(`/community/posts${qs.toString() ? `?${qs}` : ''}`);
      setData(r.data || { posts: [], courses: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the community feed');
    } finally { setLoading(false); }
  }, [api, filters]);
  useEffect(() => { load(); }, [load]);

  const open = useMemo(() => data.posts.find((p) => p.id === openId) || null, [data.posts, openId]);

  const sendReply = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || !open) return;
    setSaving(true);
    try {
      await api.post(`/posts/${open.id}/replies`, { body: reply.trim() });
      setReply('');
      await load();
      toast.success('Reply sent to the learner');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not reply'); }
    finally { setSaving(false); }
  };

  const toggleAnswered = async (post) => {
    try { await api.post(`/posts/${post.id}/resolve`); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not update'); }
  };

  const removePost = async (post) => {
    if (!window.confirm(`Delete this ${post.kind} from ${post.user_name}? Its replies go too.`)) return;
    try { await api.delete(`/posts/${post.id}`); setOpenId(null); await load(); toast.success('Deleted'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not delete'); }
  };

  const side = open ? {
    open: true, onOpenChange: () => setOpenId(null), title: open.title || `${open.kind === 'question' ? 'Question' : 'Post'} from ${open.user_name}`,
    subtitle: [open.course_title, open.topic_title, open.lesson_title].filter(Boolean).join(' · '),
    icon: open.kind === 'question' ? Question : Megaphone, width: 40, storageKey: 'pane-community', testid: 'community-thread',
    children: (
      <div className="space-y-3">
        <div className="rounded border border-border p-2.5">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[11px] font-semibold bg-shell-primary/10 text-shell-primary">{initials(open.user_name)}</span>
            <span className="text-[13px] font-medium">{open.user_name}</span>
            <span className="text-[11px] text-muted-foreground">· {when(open.created_at)}</span>
            {open.timestamp_seconds !== null && open.timestamp_seconds !== undefined && (
              <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[11px] font-mono bg-sky-50 text-sky-700"><Play className="w-3 h-3" weight="fill" />{clock(open.timestamp_seconds)}</span>
            )}
          </div>
          <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{open.body}</p>
        </div>

        {open.replies?.length > 0 && (
          <div className="space-y-2" data-testid="thread-replies">
            <Label className="text-xs">Replies ({open.replies.length})</Label>
            {open.replies.map((r) => (
              <div key={r.id} className={`rounded border p-2.5 ${isTutor(r.user_role) ? 'border-amber-200 bg-amber-50/60' : 'border-border'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-semibold ${isTutor(r.user_role) ? 'bg-amber-500 text-white' : 'bg-muted'}`}>{initials(r.user_name)}</span>
                  <span className="text-[12px] font-medium">{r.user_name}</span>
                  {isTutor(r.user_role) && <span className="inline-flex items-center gap-0.5 rounded px-1 h-4 text-[10px] font-semibold uppercase bg-amber-100 text-amber-800"><ChalkboardTeacher className="w-3 h-3" />Tutor</span>}
                  <span className="text-[11px] text-muted-foreground">· {when(r.created_at)}</span>
                </div>
                <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        )}

        <form id="community-reply" onSubmit={sendReply} className="space-y-2 border-t border-border pt-3">
          <Label>Your reply</Label>
          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="Answer the learner…" data-testid="community-reply-text"
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendReply(e); }} />
          <p className="field-hint">The learner sees this in the lesson's Community tab straight away.</p>
        </form>
      </div>
    ),
    footer: (
      <PaneFooter
        form="community-reply" submitLabel="Send reply" submitIcon={ArrowBendUpLeft} submitting={saving} submitDisabled={!reply.trim()}
        onCancel={() => setOpenId(null)} cancelLabel="Close"
        extra={(
          <div className="flex gap-2">
            <Button type="button" variant={open.resolved ? 'default' : 'outline'} size="sm" className="h-9" onClick={() => toggleAnswered(open)} data-testid="community-mark-answered">
              <CheckCircle className="w-4 h-4" /> {open.resolved ? 'Answered' : 'Mark answered'}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-9 text-destructive" onClick={() => removePost(open)}><Trash className="w-4 h-4" /></Button>
          </div>
        )}
      />
    ),
  } : null;

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
        <Page data-testid="community-page">
          <DataGrid
            title="Community & doubts"
            headerExtra={<span className="text-xs text-muted-foreground">{data.unanswered || 0} waiting for an answer</span>}
            rows={data.posts}
            loading={loading}
            onRefresh={load}
            selectable={false}
            searchKeys={['body', 'title', 'user_name', 'course_title', 'lesson_title']}
            activeFilterCount={(filters.status !== 'unanswered' ? 1 : 0) + (filters.course_id ? 1 : 0) + (filters.kind ? 1 : 0)}
            advancedSearch={(
              <div className="flex items-end gap-3 flex-wrap">
                <div className="field"><Label className="text-xs">Status</Label>
                  <select className="form-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                    <option value="unanswered">Unanswered</option>
                    <option value="answered">Answered</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="field"><Label className="text-xs">Course</Label>
                  <select className="form-select" value={filters.course_id} onChange={(e) => setFilters({ ...filters, course_id: e.target.value })} data-testid="filter-course">
                    <option value="">All courses</option>
                    {(data.courses || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>
                <div className="field"><Label className="text-xs">Type</Label>
                  <select className="form-select" value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} data-testid="filter-kind">
                    <option value="">Questions & posts</option>
                    <option value="question">Questions</option>
                    <option value="post">Posts</option>
                  </select>
                </div>
              </div>
            )}
            columns={[
              { key: 'user_name', header: 'Student', width: 150, accessor: (p) => p.user_name || '', className: 'font-medium' },
              { key: 'course_title', header: 'Course', width: 150, accessor: (p) => p.course_title || '' },
              { key: 'lesson_title', header: 'Lesson / topic', width: 200, accessor: (p) => p.lesson_title || '', render: (p) => <span className="truncate block" title={[p.topic_title, p.lesson_title].filter(Boolean).join(' · ')}>{p.lesson_title || '—'}</span> },
              { key: 'body', header: 'Question', accessor: (p) => `${p.title || ''} ${p.body}`, render: (p) => (
                <span className="block truncate" title={p.body}>
                  {p.kind === 'post' && <span className="mr-1 rounded px-1 h-4 inline-flex items-center text-[10px] font-semibold uppercase bg-muted text-muted-foreground">Post</span>}
                  {p.title ? <b>{p.title} — </b> : null}{p.body}
                </span>
              ) },
              { key: 'created_at', header: 'Posted', width: 140, accessor: (p) => p.created_at || '', render: (p) => <span className="text-muted-foreground">{when(p.created_at)}</span> },
              { key: 'reply_count', header: 'Replies', width: 110, align: 'right', accessor: (p) => p.reply_count || 0, render: (p) => (
                <span className="tabular-nums">{p.reply_count || 0}{p.staff_replied && <span className="ml-1 rounded px-1 h-4 inline-flex items-center text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">You</span>}</span>
              ) },
              { key: 'answered', header: 'Status', width: 125, accessor: (p) => (p.answered ? 'answered' : 'unanswered'), render: (p) => <StatusBadge value={p.answered ? 'answered' : 'unanswered'} /> },
            ]}
            rowTone={(p) => (p.answered ? null : 'warning')}
            onRowDoubleClick={(p) => { setOpenId(p.id); setReply(''); }}
            rowActions={(p) => (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setOpenId(p.id); setReply(''); }} data-testid={`open-thread-${p.id}`}>
                  <ArrowBendUpLeft className="w-3.5 h-3.5" /> Reply
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/instructor/courses/${p.course_id}/structure${p.node_id ? `?node=${p.node_id}` : ''}`); }} title="Open the lesson">
                  Lesson
                </Button>
              </div>
            )}
            emptyMessage={filters.status === 'unanswered' ? 'Nothing waiting — every question has been answered.' : 'No questions or posts yet. They appear here the moment a learner asks one in a lesson.'}
          />
        </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
